/**
 * Quimby Table column definitions for the Inspections listing.
 *
 * Maps the canonical column keys from inspectionsColumnPolicy.ts to
 * TableColumn<InspectionRow> descriptors.
 *
 * Column ordering matches ALL_INSPECTION_COLUMNS in inspectionsColumnPolicy.ts.
 * The selector pseudo-column is provided by the Table's `selectable` prop.
 *
 * Sticky column policy (from the contract):
 *   - `id` (primary link) → columnType: 'primary' (sticky left after selector)
 *   - No action column currently — row navigation is via the id link cell.
 *
 * Column visibility is handled externally (InspectionsListing filters the
 * full column list by visibleColumnKeys before passing to the Table).
 */
import React from 'react';
import { translate } from '@utils/i18n';
import { InspectionLinkCellRenderer } from './renderers/InspectionLinkCellRenderer';
import { InspectionDescriptionRenderer } from './renderers/InspectionDescriptionRenderer';
import { InspectionScheduledDateRenderer } from './renderers/InspectionScheduledDateRenderer';
import { InspectionStatusRenderer } from './renderers/InspectionStatusRenderer';
import { InspectionIimStatusRenderer } from './renderers/InspectionIimStatusRenderer';
const NS = 'features.inspections_index';
/**
 * All possible inspection column definitions (before visibility filtering).
 *
 * Order matches ALL_INSPECTION_COLUMNS in inspectionsColumnPolicy.ts.
 */
export const ALL_INSPECTION_COLUMN_DEFS = [
    {
        key: 'id',
        label: translate('table.headers.id', { namespace: NS }),
        column: {
            dataKey: 'id',
            label: translate('table.headers.id', { namespace: NS }),
            width: '130px',
            columnType: 'primary',
            renderer: InspectionLinkCellRenderer
        }
    },
    {
        key: 'workOrderNumber',
        label: translate('table.headers.work_order_number', { namespace: NS }),
        column: {
            dataKey: 'workOrderNumber',
            label: translate('table.headers.work_order_number', { namespace: NS })
        }
    },
    {
        key: 'referenceNumber',
        label: translate('table.headers.reference_number', { namespace: NS }),
        column: {
            dataKey: 'referenceNumber',
            label: translate('table.headers.reference_number', { namespace: NS })
        }
    },
    {
        key: 'frequency',
        label: translate('table.headers.frequency', { namespace: NS }),
        column: {
            dataKey: 'frequency',
            label: translate('table.headers.frequency', { namespace: NS }),
            width: '120px'
        }
    },
    {
        key: 'description',
        label: translate('table.headers.description', { namespace: NS }),
        column: {
            dataKey: 'description',
            label: translate('table.headers.description', { namespace: NS }),
            width: '250px',
            renderer: InspectionDescriptionRenderer
        }
    },
    {
        key: 'scheduledDate',
        label: translate('table.headers.scheduled_date', { namespace: NS }),
        column: {
            dataKey: 'scheduledDate',
            label: translate('table.headers.scheduled_date', { namespace: NS }),
            width: '130px',
            renderer: InspectionScheduledDateRenderer
        }
    },
    {
        key: 'inspectionDate',
        label: translate('table.headers.inspection_date', { namespace: NS }),
        column: {
            dataKey: 'inspectionDate',
            label: translate('table.headers.inspection_date', { namespace: NS }),
            width: '120px'
        }
    },
    {
        key: 'dueDate',
        label: translate('table.headers.due_date', { namespace: NS }),
        column: {
            dataKey: 'dueDate',
            label: translate('table.headers.due_date', { namespace: NS }),
            width: '120px'
        }
    },
    {
        key: 'assignedTo',
        label: translate('table.headers.assigned_to', { namespace: NS }),
        column: {
            dataKey: 'technician',
            label: translate('table.headers.assigned_to', { namespace: NS }),
            width: '150px',
            renderer: InspectionLinkCellRenderer
        }
    },
    {
        key: 'building',
        label: translate('table.headers.building', { namespace: NS }),
        column: {
            dataKey: 'building',
            label: translate('table.headers.building', { namespace: NS }),
            width: '250px',
            renderer: InspectionLinkCellRenderer
        }
    },
    {
        key: 'account',
        label: translate('table.headers.account', { namespace: NS }),
        column: {
            dataKey: 'account',
            label: translate('table.headers.account', { namespace: NS }),
            width: '200px',
            renderer: InspectionLinkCellRenderer
        }
    },
    {
        key: 'zipCode',
        label: translate('table.headers.zip_code', { namespace: NS }),
        column: {
            dataKey: 'zipCode',
            label: translate('table.headers.zip_code', { namespace: NS }),
            width: '100px'
        }
    },
    {
        key: 'status',
        label: translate('table.headers.status', { namespace: NS }),
        column: {
            dataKey: 'status',
            label: translate('table.headers.status', { namespace: NS }),
            renderer: InspectionStatusRenderer
        }
    },
    {
        key: 'inspectionType',
        label: translate('table.headers.inspection_type', { namespace: NS }),
        column: {
            dataKey: 'inspectionType',
            label: translate('table.headers.inspection_type', { namespace: NS }),
            width: '150px'
        }
    },
    {
        key: 'visits',
        label: translate('table.headers.visits', { namespace: NS }),
        column: {
            dataKey: 'visits',
            label: translate('table.headers.visits', { namespace: NS }),
            width: '260px'
        }
    },
    {
        key: 'unresolvedIssues',
        label: translate('table.headers.unresolved_issues', { namespace: NS }),
        iimOnly: true,
        column: {
            dataKey: 'unresolvedIssues',
            label: (React.createElement("span", { title: translate('table.headers.unresolved_issues', { namespace: NS }), style: { whiteSpace: 'nowrap' } }, translate('table.headers.unresolved_issues', { namespace: NS }))),
            width: '180px',
            renderer: InspectionIimStatusRenderer
        }
    }
];
const COLUMN_DEF_BY_KEY = new Map(ALL_INSPECTION_COLUMN_DEFS.map((d) => [d.key, d]));
/**
 * Returns ordered TableColumn definitions for the given visible column keys.
 * Preserves the canonical ordering from ALL_INSPECTION_COLUMN_DEFS.
 */
export function buildVisibleColumns(visibleColumnKeys) {
    const keySet = new Set(visibleColumnKeys);
    return ALL_INSPECTION_COLUMN_DEFS.filter((def) => keySet.has(def.key)).map((def) => def.column);
}
/**
 * Returns ColumnVisibilityItem descriptors for the ColumnVisibilityControl,
 * filtered to the allowed keys and excluding iimOnly columns when iimEnabled=false.
 */
export function buildColumnVisibilityItems(allowedKeys, requiredKeys, iimEnabled) {
    const allowedSet = new Set(allowedKeys);
    const requiredSet = new Set(requiredKeys);
    return ALL_INSPECTION_COLUMN_DEFS.filter((def) => {
        if (!allowedSet.has(def.key))
            return false;
        if (def.iimOnly && !iimEnabled)
            return false;
        return true;
    }).map((def) => ({
        key: def.key,
        label: def.label,
        required: requiredSet.has(def.key)
    }));
}
